import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { X, Home, Camera, BookOpen, LogOut, Grape, Leaf, Sprout, Info, CloudSun, Link as LinkIcon, Scissors, MessageSquare, Inbox, IdCard, Shield, FlaskConical, Check, Wheat } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES, setUserLanguage, LANGUAGE_SWITCHER_ENABLED } from "../utils/language";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Drawer({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Fetch unread suggestions count when drawer opens (admin only)
  useEffect(() => {
    if (!open || !user?.is_admin) return;
    axios.get(`${API}/suggestions/unread-count`, { withCredentials: true })
      .then((r) => setUnread(r.data?.unread || 0))
      .catch(() => setUnread(0));
  }, [open, user?.is_admin]);

  // Lock scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (to) => { onClose(); navigate(to); };

  const items = [
    { to: "/dashboard", icon: Home, label: t("drawer.items.vineyards"), testid: "drawer-vineyards" },
    { to: "/dpi", icon: Shield, label: t("drawer.items.dpi"), testid: "drawer-dpi" },
    { to: "/meteo", icon: CloudSun, label: t("drawer.items.weather"), testid: "drawer-meteo" },
    { to: "/diagnosi", icon: Camera, label: t("drawer.items.diagnose"), testid: "drawer-diagnose" },
    { to: "/concimi", icon: Sprout, label: t("drawer.items.fertilizer"), testid: "drawer-fertilizer" },
    { to: "/gestione-vite", icon: Scissors, label: t("drawer.items.pruning"), testid: "drawer-gestione" },
    { to: "/quaderno", icon: BookOpen, label: t("drawer.items.journal"), testid: "drawer-journal" },
    { to: "/altre-colture", icon: Wheat, label: t("drawer.items.otherCrops"), testid: "drawer-other-crops" },
    { to: "/catalogo", icon: FlaskConical, label: t("drawer.items.catalog"), testid: "drawer-catalog" },
    { to: "/forum", icon: MessageSquare, label: t("drawer.items.forum"), testid: "drawer-forum" },
    { to: "/link-utili", icon: LinkIcon, label: t("drawer.items.usefulLinks"), testid: "drawer-links" },
    { to: "/profilo", icon: IdCard, label: t("drawer.items.profile"), testid: "drawer-profile" },
    { to: "/suggerimenti", icon: Inbox, label: user?.is_admin ? t("drawer.items.suggestionsReceived") : t("drawer.items.sendSuggestion"), testid: "drawer-suggestions", badge: user?.is_admin ? unread : 0 },
    { to: "/info", icon: Info, label: t("drawer.items.about"), testid: "drawer-about" },
  ];

  return (
    <>
      <div
        className={`vs-drawer-backdrop ${open ? "open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
        data-testid="drawer-backdrop"
      />
      <aside
        className={`vs-drawer ${open ? "open" : ""}`}
        role="dialog"
        aria-label={t("drawer.ariaLabel")}
        aria-modal="true"
        data-testid="drawer"
      >
        {/* Header with gradient and close */}
        <div style={{ background: "var(--gradient-vine)", color: "white", padding: "20px 18px", position: "relative" }}>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-lg"
            style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.25)", color: "white" }}
            aria-label={t("drawer.closeAriaLabel")}
            data-testid="drawer-close"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.18)", border: "2px solid rgba(255,255,255,0.3)" }}
            >
              <Leaf size={20} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <div className="heading font-extrabold text-lg leading-tight">Il Mio Vigneto</div>
              <div className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>{t("drawer.tagline")}</div>
            </div>
          </div>
          {user && (
            <div
              className="mt-3 p-3 rounded-xl flex items-center gap-3"
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)" }}
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                  style={{ border: "2px solid rgba(255,255,255,0.5)" }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--gold)" }}>
                  <Grape size={18} />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold truncate">{user.name}</div>
                <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.8)" }}>{user.email}</div>
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end
                onClick={() => go(it.to)}
                className={({ isActive }) => `vs-drawer-link ${isActive ? "active" : ""}`}
                data-testid={it.testid}
              >
                <Icon size={22} strokeWidth={2.2} />
                <span className="flex-1">{it.label}</span>
                {it.badge > 0 && (
                  <span
                    className="ml-auto text-xs font-extrabold px-2 py-0.5 rounded-full"
                    style={{ background: "var(--gold)", color: "#5a3500", border: "1.5px solid #7a4b00" }}
                    data-testid={`${it.testid}-badge`}
                  >
                    {it.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Language */}
        {LANGUAGE_SWITCHER_ENABLED && (
          <div style={{ padding: "0 16px 14px" }} data-testid="drawer-language-section">
            <div className="text-[11px] font-extrabold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
              Lingua / Langue
            </div>
            <div className="flex gap-2">
              {LANGUAGES.map((l) => {
                const active = i18n.language === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => setUserLanguage(i18n, l.code)}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-xl font-bold text-sm"
                    style={{
                      background: active ? "var(--primary)" : "var(--surface)",
                      color: active ? "white" : "var(--text-primary)",
                      border: `2px solid ${active ? "var(--primary)" : "var(--border)"}`,
                    }}
                    aria-pressed={active}
                    data-testid={`drawer-lang-${l.code}`}
                  >
                    <span aria-hidden="true">{l.flag}</span> {l.label}
                    {active && <Check size={14} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "12px 16px 18px", borderTop: "2px solid var(--border)" }}>
          <button
            onClick={() => { onClose(); logout(); }}
            className="vs-btn-secondary w-full inline-flex items-center justify-center gap-2"
            data-testid="drawer-logout"
          >
            <LogOut size={16} /> {t("drawer.logout")}
          </button>
          <div className="text-center text-xs mt-3" style={{ color: "var(--text-muted)" }}>
            Il Mio Vigneto · v1.0
          </div>
        </div>
      </aside>
    </>
  );
}
